import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { RootState } from './store';
import lightTheme, { darkTheme } from './theme';

import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import LoanApplicationForm from './components/LoanApplicationForm';
import ApplicationList from './components/ApplicationList';
import ApplicationDetail from './components/ApplicationDetail';
import RulesManagement from './components/RulesManagement';
import LoginPage from './components/LoginPage';
import PrivateRoute from './components/PrivateRoute';
import ReportsPage from './components/ReportsPage';
import ApiDocumentation from './components/ApiDocumentation';
import AuditLogPage from './components/AuditLogPage';
import { useAppDispatch } from './store/hooks';
import { fetchRules, fetchSmeRules } from './store/configSlice';
import { fetchAuditLogs } from './store/auditLogSlice';
import { fetchApplications } from './store/applicationSlice';
import RoleDashboard from './components/RoleDashboard';
import RoleRoute from './components/RoleRoute';
import MerchantRegistration from './components/merchant/MerchantRegistration';
import MerchantLifecycle from './components/merchant/MerchantLifecycle';
import NonPerformingLoans from './components/bank/NonPerformingLoans';
import MerchantProfile from './components/bank/MerchantProfile';
import CreditBureau from './components/bank/CreditBureau';
import SettingsPage from './components/SettingsPage';
import TransactionDetails from './components/TransactionDetails';
import { LanguageProvider } from './i18n/LanguageContext';
import UsersManagement from './components/UsersManagement';
import NotificationsCenter from './components/NotificationsCenter';
import CreditDecisioningEngine from './components/CreditDecisioningEngine';
import DataIngestionPage from './components/DataIngestionPage';
import ModelPerformancePage from './components/ModelPerformancePage';

const App: React.FC = () => {
  const darkMode = useSelector((state: RootState) => state.config.darkMode);
  const theme = darkMode ? darkTheme : lightTheme;
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchRules());
    dispatch(fetchSmeRules());
    dispatch(fetchAuditLogs());
    dispatch(fetchApplications());
  }, [dispatch]);

  return (
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <Layout>
              <Routes>
                <Route element={<PrivateRoute />}>
                  <Route path="/" element={<RoleDashboard />} />
                  <Route element={<RoleRoute allowed={["merchant"]} />}>
                    <Route path="/merchant/register" element={<MerchantRegistration />} />
                    <Route path="/merchant/lifecycle/:id" element={<MerchantLifecycle />} />
                  </Route>
                  <Route element={<RoleRoute allowed={["wave"]} />}>
                    {/* Wave-only routes can be added here */}
                  </Route>
                  <Route element={<RoleRoute allowed={["bank", "wave"]} />}>
                    <Route path="/bank/np-loans" element={<NonPerformingLoans />} />
                    <Route path="/bank/merchant/:merchantId" element={<MerchantProfile />} />
                    <Route path="/bank/credit-bureau" element={<CreditBureau />} />
                  </Route>
                  <Route path="/status" element={<ApplicationList />} />
                  <Route path="/application/:id" element={<ApplicationDetail />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/audit-log" element={<AuditLogPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/transactions" element={<TransactionDetails />} />
                  
                  {/* Admin-only routes (Platform admin role) */}
                  <Route element={<RoleRoute allowed={["bank"]} />}>
                    <Route path="/rules" element={<RulesManagement />} />
                    <Route path="/decisioning" element={<CreditDecisioningEngine />} />
                    <Route path="/data-ingestion" element={<DataIngestionPage />} />
                    <Route path="/model-performance" element={<ModelPerformancePage />} />
                    <Route path="/api-docs" element={<ApiDocumentation />} />
                    <Route path="/admin/users" element={<UsersManagement />} />
                    <Route path="/admin/notifications" element={<NotificationsCenter />} />
                  </Route>
                </Route>
              </Routes>
            </Layout>
          }
        />
      </Routes>
      </ThemeProvider>
    </LanguageProvider>
  );
};

export default App;